"use client";

import React from "react";
import { useTableRowActions } from "./hooks/useTableRowActions";
import { MobileActionsDropdown } from "./components/SimpleTable/MobileActionsDropdown";
import { DesktopActionsView } from "./components/SimpleTable/DesktopActionsView";
import type {
  DetailAction,
  DetailActionContext,
  BaseEntity,
} from "@/components/shared/UniversalDetailPage/types/detailPage";

// ============================================================================
// TYPES
// ============================================================================

interface TableRowActionsProps<T extends BaseEntity> {
  /** The item being acted upon */
  item: T;
  /** Available actions */
  actions: DetailAction<T>[];
  /** Action context for executing actions */
  actionContext: DetailActionContext<T>;
  /** Maximum number of actions to show before using dropdown */
  maxVisibleActions?: number;
  /** Additional CSS classes */
  className?: string;
  /** Compact mode for icon-only display */
  compact?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function TableRowActions<T extends BaseEntity>({
  item,
  actions,
  actionContext,
  maxVisibleActions = 2,
  className = "",
  compact = false,
}: TableRowActionsProps<T>) {
  const { visibleActions, actionGroups, loadingActions, isMobile, handleAction } =
    useTableRowActions(item, actions, actionContext, maxVisibleActions);

  if (visibleActions.length === 0) {
    return null;
  }

  // Always return the same JSX structure to ensure consistent hook calls.
  // Conditional rendering lives inside the return, not as early returns.
  return (
    <>
      {isMobile ? (
        <MobileActionsDropdown
          item={item}
          visibleActions={visibleActions}
          loadingActions={loadingActions}
          onAction={handleAction}
          className={className}
        />
      ) : (
        <DesktopActionsView
          item={item}
          visibleActions={visibleActions}
          actionGroups={actionGroups}
          loadingActions={loadingActions}
          onAction={handleAction}
          compact={compact}
          className={className}
        />
      )}
    </>
  );
}

export { TableRowActions as TableRowActionsComponent };
